import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock,
  Zap,
  Edit3,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { formatLapTime, formatSpeed, formatTimeDiff } from "@/utils/timeFormat";
import type { Lap } from "@/types";

interface LapCardProps {
  lap: Lap;
  bestLapTime?: number;
  onEdit?: (lap: Lap) => void;
  onDelete?: (lapId: string) => void;
  showComparison?: boolean;
}

export default function LapCard({
  lap,
  bestLapTime,
  onEdit,
  onDelete,
  showComparison = true,
}: LapCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation(["pages", "common"]);
  const isBestLap = bestLapTime && lap.lapTime === bestLapTime;
  const timeDiff =
    bestLapTime && showComparison
      ? formatTimeDiff(lap.lapTime, bestLapTime)
      : null;

  return (
    <div
 className={`bg-white rounded-lg border transition-all hover:border-secondary-300 ${
        isBestLap
          ? "border-yellow-400 bg-yellow-50"
          : !lap.isValid
            ? "border-red-300 bg-red-50"
            : "border-secondary-200"
      }`}
    >
 <div className={isExpanded ? "p-4" : "p-3"}>
        {/* Collapsed Header */}
        <div
 className="flex justify-between items-center cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
 <div className="flex items-center space-x-3 flex-1">
 <div className="flex items-center space-x-2">
              <span
 className={`text-lg font-bold ${
                  isBestLap
                    ? "text-yellow-700"
                    : !lap.isValid
                      ? "text-red-600"
                      : "text-secondary-900"
                }`}
              >
                #{lap.lapNumber}
              </span>
              {isBestLap && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                  {t("pages:sessions.detail.laps.card.best")}
                </span>
              )}
              {!lap.isValid && (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {t("pages:sessions.detail.laps.card.invalid")}
                </span>
              )}
            </div>

            {/* Collapsed Lap Time */}
            {!isExpanded && (
 <div className="flex items-center space-x-2">
 <Clock className="h-4 w-4 text-secondary-400" />
                <span
 className={`text-lg font-mono font-bold ${
                    isBestLap
                      ? "text-yellow-700"
                      : !lap.isValid
                        ? "text-red-600"
                        : "text-secondary-900"
                  }`}
                >
                  {formatLapTime(lap.lapTime)}
                </span>
                {timeDiff && timeDiff !== "±0.00" && (
                  <span
 className={`text-sm font-mono ${
                      timeDiff.startsWith("+")
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    ({timeDiff})
                  </span>
                )}
              </div>
            )}
          </div>

 <div className="flex items-center space-x-1">
            {isExpanded && (
              <>
                {onEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(lap);
                    }}
                    className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                    title={t("pages:sessions.detail.laps.card.editLap")}
                    aria-label={t("pages:sessions.detail.laps.card.editLap")}
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(lap.id);
                    }}
                    className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title={t("pages:sessions.detail.laps.card.deleteLap")}
                    aria-label={t("pages:sessions.detail.laps.card.deleteLap")}
                  >
 <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
            <button
 className="p-1 text-secondary-400 hover:text-secondary-600 transition-colors"
              aria-label={isExpanded ? t("pages:sessions.detail.laps.card.collapse") : t("pages:sessions.detail.laps.card.expand")}
            >
              {isExpanded ? (
 <ChevronUp className="h-4 w-4" />
              ) : (
 <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
 <div className="mt-4 space-y-4">
            {/* Lap Time - Expanded */}
            <div>
 <div className="flex items-center space-x-2">
 <Clock className="h-5 w-5 text-secondary-400" />
                <span
 className={`text-2xl font-mono font-bold ${
                    isBestLap
                      ? "text-yellow-700"
                      : !lap.isValid
                        ? "text-red-600"
                        : "text-secondary-900"
                  }`}
                >
                  {formatLapTime(lap.lapTime)}
                </span>
                {timeDiff && timeDiff !== "±0.00" && (
                  <span
 className={`text-sm font-mono ${
                      timeDiff.startsWith("+")
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    ({timeDiff})
                  </span>
                )}
              </div>
            </div>

            {/* Sector Times */}
            {lap.sectorTimes && lap.sectorTimes.length > 0 && (
              <div>
                <div className="text-xs text-secondary-500 mb-1">{t("pages:sessions.detail.laps.card.sectorTimes")}</div>
                <div className="flex space-x-2 text-sm font-mono">
                  {lap.sectorTimes.map((sectorTime, index) => (
                    <span key={index} className="text-secondary-700">
                      {t("pages:sessions.detail.laps.card.sectorShort", { index: index + 1 })}: {formatLapTime(sectorTime)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Speed and RPM */}
 <div className="flex flex-wrap gap-4 text-sm">
              {lap.maxSpeed && (
 <div className="flex items-center space-x-1">
 <Zap className="h-4 w-4 text-secondary-400" />
                  <div>
                    <div className="text-xs text-secondary-500">{t("pages:sessions.detail.laps.card.maxSpeed")}</div>
                    <div className="font-medium text-secondary-900">
                      {formatSpeed(lap.maxSpeed)}
                    </div>
                  </div>
                </div>
              )}

              {lap.minSpeed && (
                <div className="flex items-center space-x-1">
                  <Zap className="h-4 w-4 text-secondary-400" />
                  <div>
                    <div className="text-xs text-secondary-500">{t("pages:sessions.detail.laps.card.minSpeed")}</div>
                    <div className="font-medium text-secondary-900">
                      {formatSpeed(lap.minSpeed)}
                    </div>
                  </div>
                </div>
              )}

              {lap.avgSpeed && (
                <div className="flex items-center space-x-1">
                  <Clock className="h-4 w-4 text-secondary-400" />
                  <div>
                    <div className="text-xs text-secondary-500">{t("pages:sessions.detail.laps.card.avgSpeed")}</div>
                    <div className="font-medium text-secondary-900">
                      {formatSpeed(lap.avgSpeed)}
                    </div>
                  </div>
                </div>
              )}

              {lap.maxRpm && (
                <div className="flex items-center space-x-1">
                  <Zap className="h-4 w-4 text-secondary-400" />
                  <div>
                    <div className="text-xs text-secondary-500">{t("pages:sessions.detail.laps.card.maxRpm")}</div>
 <div className="font-medium text-secondary-900">
                      {lap.maxRpm.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {lap.notes && (
 <div className="pt-3 border-t border-secondary-100">
              <div className="text-xs text-secondary-500 mb-1">{t("common:notes")}</div>
 <p className="text-sm text-secondary-700 bg-secondary-50 rounded p-2">
                  {lap.notes}
                </p>
              </div>
            )}

            {/* Metadata */}
            <div className="pt-3 border-t border-secondary-100 text-xs text-secondary-500">
              {t("pages:sessions.detail.laps.card.recorded", { date: new Date(lap.createdAt).toLocaleString() })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
